import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useGetApplications } from "@/hooks/useApplications";
import { humanizeString } from "@/lib/utils";
import { Layers, Search, Settings2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import LoadingSpinner from "../Common/Spinner";
import { ProjectSettingsDialog } from "./ProjectSettingsDialog";

export function ProjectsPage() {
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const limit = 8;

  const projectsQuery = useGetApplications(limit, offset, searchTerm, null);
  const projectGroups = projectsQuery.data?.items ?? [];
  const hasNext = projectsQuery.data?.hasNext ?? false;

  const selectedProject = useMemo(() => {
    if (!selectedProjectId) {
      return null;
    }
    return (
      projectGroups.find(({ project }) => project.id === selectedProjectId)
        ?.project ?? null
    );
  }, [projectGroups, selectedProjectId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(inputValue);
      setOffset(0);
    }, 400);
    return () => clearTimeout(handler);
  }, [inputValue]);

  const handleNext = () => {
    if (hasNext) setOffset((prev) => prev + limit);
  };

  const handlePrev = () => {
    if (offset > 0) setOffset((prev) => Math.max(prev - limit, 0));
  };

  return (
    <div className="min-h-full w-full pb-4">
      <div className="border-b border-slate-800 bg-slate-900/90"></div>
      <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6 lg:space-y-8 w-full">
        <div className="flex flex-col gap-3 md:gap-4 sm:flex-row sm:items-center sm:justify-between pt-12 lg:pt-0">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-1">
              Projects
            </h1>
            <p className="text-sm text-slate-400">
              Configure shared project settings without touching existing
              deploys.
            </p>
          </div>
        </div>

        <Card className="holo-card border-cyan-500/40">
          <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6 lg:p-8">
            <div className="flex flex-col gap-3 md:gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full lg:max-w-sm">
                <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-cyan-300/60" />
                <Input
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder="Search by repo or project name"
                  className="pl-10 md:pl-11 bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500 h-11"
                />
              </div>
              <div className="flex gap-2 text-xs uppercase tracking-[0.2em] md:tracking-[0.3em] text-cyan-300/70">
                <span>Showing</span>
                <span>{projectsQuery.data?.total ?? 0} projects</span>
              </div>
            </div>

            {projectsQuery.isLoading ? (
              <LoadingSpinner />
            ) : projectGroups.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-cyan-500/40 p-12 text-center text-slate-300">
                <Layers className="mx-auto h-10 w-10 mb-4 text-cyan-400" />
                <p className="font-semibold mb-1">No projects yet</p>
                <p className="text-sm">
                  Create a deploy to generate your first project.
                </p>
              </div>
            ) : (
              <div className="space-y-4 md:space-y-5">
                {projectGroups.map(({ project }) => (
                  <div
                    key={project.id}
                    className="rounded-xl border border-cyan-500/30 bg-slate-950/80 p-4 md:p-5 shadow-lg shadow-cyan-500/10 space-y-4 md:space-y-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs uppercase tracking-[0.2em] md:tracking-[0.3em] text-cyan-300/70">
                          Project
                        </p>
                        <h3 className="text-lg md:text-xl font-semibold truncate">
                          {project.name}
                        </h3>
                        <p className="text-xs md:text-sm text-slate-400 break-all">
                          {project.repoUri ?? "Repository pending connection"}
                        </p>
                        {project.config?.rootDirectory ? (
                          <p className="text-xs text-slate-500">
                            Root: {project.config.rootDirectory}
                          </p>
                        ) : null}
                        {project.config?.deployType ? (
                          <p className="text-xs text-slate-500">
                            Type: {humanizeString(project.config.deployType)}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Button
                          variant="outline"
                          className="border-cyan-500/40 text-cyan-200 hover:text-cyan-100 text-xs"
                          onClick={() => setSelectedProjectId(project.id)}
                        >
                          <Settings2 className="h-4 w-4 mr-2" />
                          Configure
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2 text-sm text-slate-400">
              <div className="text-center sm:text-left">
                Page {offset / limit + 1} ·{" "}
                <span>{projectsQuery.data?.total ?? 0} projects</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  disabled={offset === 0}
                  onClick={handlePrev}
                  className="text-slate-200 hover:text-white disabled:opacity-40 flex-1 sm:flex-initial"
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  disabled={!hasNext}
                  onClick={handleNext}
                  className="text-slate-200 hover:text-white disabled:opacity-40 flex-1 sm:flex-initial"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <ProjectSettingsDialog
        project={selectedProject}
        open={selectedProjectId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedProjectId(null);
          }
        }}
      />
    </div>
  );
}
